import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { getImageUrl } from '../../config/constants';
import AlertDialog from './AlertDialog';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, getTotalPrice, getTotalItems } = useCart();

  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleViewCart = () => {
    navigate('/cart');
    onClose();
  };

  const handleCheckout = () => {
    navigate('/checkout');
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[450px] bg-canvas-primary shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-stroke-secondary">
            <div>
              <h2 className="text-2xl font-bold text-fg-primary">Carrito</h2>
              <p className="text-sm text-fg-secondary mt-1">
                {getTotalItems()} {getTotalItems() === 1 ? 'artículo' : 'artículos'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-canvas-secondary transition-colors text-fg-primary"
              aria-label="Close cart"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <svg className="w-24 h-24 text-fg-tertiary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <h3 className="text-xl font-bold text-fg-primary mb-2">Tu carrito está vacío</h3>
                <p className="text-fg-secondary mb-6">¡Agrega productos para empezar!</p>
                <button
                  onClick={onClose}
                  className="bg-fg-highlight-primary hover:bg-fg-highlight-secondary text-white font-bold py-2 px-6 rounded-full transition-all duration-300"
                >
                  Continuar Comprando
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => {
                  const product = item.product;
                  const productImage = (() => {
                    const images = product.images;
                    if (images && Array.isArray(images) && images.length > 0) {
                      const firstImage = images.find((img: any) => img.type === 'image');
                      return firstImage ? getImageUrl(firstImage.url) : product.image_url ? getImageUrl(product.image_url) : '';
                    }
                    return product.image_url ? getImageUrl(product.image_url) : '';
                  })();

                  const effectivePrice = product.discount_price || product.price;

                  return (
                    <div key={item.id} className="flex gap-4 pb-4 border-b border-stroke-secondary last:border-0">
                      {/* Image */}
                      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-canvas-secondary">
                        {productImage ? (
                          <img src={productImage} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-fg-primary mb-1 line-clamp-2 text-sm">{product.name}</h3>

                        {/* Price */}
                        <div className="flex items-center gap-2 mb-2">
                          {product.discount_price && (
                            <span className="text-xs text-fg-tertiary line-through">
                              {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(product.price)}
                            </span>
                          )}
                          <span className="text-sm font-bold text-fg-highlight-primary">
                            {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(effectivePrice)}
                          </span>
                        </div>

                        {/* Talla and Largo Badges */}
                        {(product.selectedTalla || product.selectedLargo) && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {product.selectedTalla && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-300 rounded text-xs font-bold text-purple-900">
                                <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                                {product.selectedTalla}
                              </span>
                            )}
                            {product.selectedLargo && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-300 rounded text-xs font-bold text-blue-900 capitalize">
                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                                {product.selectedLargo}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 bg-canvas-secondary rounded-lg p-1">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-6 h-6 flex items-center justify-center rounded hover:bg-canvas-primary transition-colors text-fg-primary"
                              disabled={item.quantity <= 1}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                              </svg>
                            </button>
                            <span className="w-8 text-center text-sm font-bold text-fg-primary">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-6 h-6 flex items-center justify-center rounded hover:bg-canvas-primary transition-colors text-fg-primary"
                              disabled={item.quantity >= product.stock_quantity}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          </div>

                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-red-600 hover:text-red-700 transition-colors"
                            aria-label="Remove item"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-stroke-secondary p-6 bg-canvas-secondary">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold text-fg-primary">Subtotal</span>
                <span className="text-2xl font-bold text-fg-highlight-primary">
                  {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(getTotalPrice())}
                </span>
              </div>
              <p className="text-xs text-fg-secondary mb-4">Los impuestos y el envío se calculan en el pago</p>
              <div className="space-y-3">
                <button
                  onClick={handleCheckout}
                  className="w-full bg-fg-highlight-primary hover:bg-fg-highlight-secondary text-white font-bold py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  Proceder al Pago
                </button>
                <button
                  onClick={handleViewCart}
                  className="w-full bg-canvas-primary hover:bg-canvas-tertiary text-fg-primary font-semibold py-3 rounded-full border border-stroke-secondary transition-all duration-300"
                >
                  Ver Carrito Completo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onConfirm={() => setAlertDialog({ ...alertDialog, isOpen: false })}
        confirmText="Aceptar"
      />
    </>
  );
};

export default CartDrawer;
